import { createTranslationRef } from '@backstage/core-plugin-api/alpha';

/** @alpha */
export const newsMessagesGerman = {
  common: {
    locale: 'de-DE',
    confirm: 'Bestätigen',
    cancel: 'Abbrechen',
    close: 'Schließen',
    delete: 'Löschen',
    edit: 'Bearbeiten',
    save: 'Speichern',
    immediately: 'Sofort',
    loading: 'Laden...',
    loadingImage: 'Bild wird geladen...',
    paginationInfo: 'von',
    by: 'von',
  },

  lockDuration: {
    seconds: 'seit {{count}} Sekunden',
    minutes: 'seit {{count}} Minuten',
    hours: 'seit {{count}} Stunden',
  },

  newsPage: {
    title: 'Newsportal',
    editorial: 'Redaktion',
    noNews: 'Zurzeit sind keine Neuigkeiten vorhanden.',
    readMore: 'zum Artikel',
    rowsPerPage: 'Artikel pro Seite:',
    errors: {
      loadingArticles: 'Fehler beim Laden der Artikel',
    },
    showMoreTags: 'Themenbereiche anzeigen',
    showLessTags: 'Themenbereiche ausblenden',
    newMembers: {
      title: 'Die neusten Puzzle Members',
    },
    backToOverview: 'zurück zur Übersicht',
  },

  newsDetailPage: {
    error: {
      title: 'Fehler',
    },
    notFound: {
      title: 'Nicht gefunden',
    },
    backToOverview: 'Zurück zur Übersicht',
    breadcrumbs: {
      news: 'Nachrichten',
    },
  },

  members: {
    cardTitle: 'Neue Member(s)',
    cardHelper:
      'Diesen Bereich musst du nur ausklappen und ausfüllen, wenn du einen neuen Member ankündigen möchtest. Dadurch wird ein Foto der Person in der Spalte `Die neusten Puzzle Members` auf der News-Übersicht hinzugefügt.',
    cardToggle: 'Member-Bereich anzeigen',
    nameLabel: 'Name des Members',
    avatarGuide:
      'Anleitung für das Erstellen eines Member Thumbnails (mit GIMP) findest du im Userguide: Docs > Puzzle Backstage > Features > Newsportal > Userguide > Neue Members > Anleitung: Thumbnail erstellen mit GIMP',
    cardAddButton: 'Hinzufügen',
    confirmDelete: {
      title: 'Member löschen',
      message: 'Member "{{name}}" wirklich löschen?',
    },
    validation: {
      nameRequired: 'Name ist erforderlich',
      imageRequired: 'Bild ist erforderlich',
      imageTooLarge: 'Bild darf maximal 500 KB gross sein',
      imageDimensions: 'Bild darf maximal 200×200 Pixel gross sein',
    },
    noMembers: 'Noch keine Members vorhanden.',
    pendingLabel: 'INFO: Wird erst beim Speichern in der DB gesichert',
    helper: 'Image darf maximal 200x200 Pixel und 500 KB gross sein',
  },

  adminPage: {
    title: 'Redaktion',
    newArticle: 'Neuer Beitrag',
    manageTags: 'Tags verwalten',
    allArticles: 'Alle Beiträge',
    preview: 'Vorschau ansehen',
    searchPlaceholder: 'Suche nach Titel, Autor',
    columns: {
      id: 'ID',
      title: 'Titel',
      author: 'Autor',
      lock: 'Lock',
      status: 'Status',
      date: 'Datum',
      actions: 'Aktionen',
    },
    status: {
      draft: 'Entwurf',
      planned: 'Geplant',
      published: 'Öffentlich',
      archived: 'Archiviert',
    },
    lockInfo: 'Gesperrt von {{user}} seit {{time}}',
    confirmPublish: {
      title: 'Veröffentlichen',
      message: '"{{title}}" jetzt sofort veröffentlichen?',
    },
    confirmUnlock: {
      title: 'Lock entfernen',
      message:
        'Lock von Artikel "{{ articleTitle }}" entfernen? Der aktuelle Bearbeiter ({{ lockUser }}) verliert möglicherweise Daten!',
    },
    confirmDelete: {
      title: 'Löschen',
      message: 'Den Artikel "{{ articleTitle }}" wirklich löschen?',
    },
    actions: {
      unlock: 'Unlock',
    },
  },

  editPage: {
    titleNew: 'Neuer Beitrag',
    titleEdit: 'Beitrag bearbeiten',
    content: {
      title: 'Inhalt',
      titleLabel: 'Titel',
      help: {
        title: 'Userguide & Hilfe zur Erstellung von Artikeln',
        userguideLink: 'Detailierte Informationen findest Du im',
        userguide: 'Userguide',
        mdEditorInformation:
          'Die ist ein Markdown Editor. Du kannst Markdown-Syntax (z.B. für Titel) verwenden, um deinen Text zu formatieren.',
        imagesInfo: 'Bilder können per Drag & Drop in den Editor eingefügt werden. (Maximale Dateigröße: {{size}} MB)',
        videosInfo: 'Videos müssen in folgendem Format eingefügt werden:',
        videoExample: 'Beispiel:',
        videoImportant: 'Wichtig: das ! muss unbedingt mit angegeben werden.',
        videoShorts:
          'Um YouTube Short Videos einzufügen, muss die Video-ID in den Standardbefehl übertragen werden:\n' +
          '![video](https://www.youtube.com/shorts/fzHhiZ5eHNs) wird zu\n' +
          '![video](https://www.youtube.com/watch?v=fzHhiZ5eHNs)',
      },
      placeholder: 'Schreibe hier deinen Text oder ziehe Bilder per Drag & Drop hinein...',
    },
    settings: {
      title: 'Einstellungen',
      onBehalfOfLabel: 'Im Auftrag von',
      onBehalfOfHelper:
        'Muss nur ausgefüllt werden, wenn der Beitrag im Auftrag einer anderen Person veröffentlicht wird.',
      tagsLabel: 'Tags',
    },
    publishing: {
      title: 'Veröffentlichung',
      statusLabel: 'Status',
      statusDraft: 'Entwurf (Unsichtbar)',
      statusPlanned: 'Planen (Zeitgesteuert)',
      statusNow: 'Sofort veröffentlichen',
      dateTimeLabel: 'Zeitpunkt',
      publishNow: 'Jetzt veröffentlichen',
      updatePublished: 'Veröffentlichten Artikel aktualisieren',
      noNotificationHint: 'Beim Aktualisieren des Artikels werden keine Chat- oder E-Mail-Benachrichtigungen versandt.',
    },
    alerts: {
      checkTitle: 'Zum Speichern bitte Titel eingeben',
      saveFailed: 'Fehler beim Speichern:',
      uploadFailed: 'Upload fehlgeschlagen!',
      imageTooLarge: 'Bild ist zu gross',
      noPermission: 'Keine Berechtigung: Sie können nur Ihre eigenen Artikel bearbeiten.',
    },
    validation: {
      title: {
        mandatory: 'Titel ist obligatorisch',
        length: 'Titel darf nicht länger als 200 Zeichen sein',
      },
      onBehalfOf: 'On behalf of darf nicht länger als 100 Zeichen sein',
    },
  },

  tagsDialog: {
    title: 'Tags verwalten',
    newTagLabel: 'Neuer Tag (z.B. HR, Event)',
    add: 'Hinzufügen',
    loadError: 'Fehler beim Laden:',
    noTags: 'Noch keine Tags vorhanden.',
    confirmDelete: {
      title: 'Tag löschen',
      message: 'Tag "{{name}}" wirklich löschen?',
    },
    validations: {
      mandatory: 'Tag ist obligatorisch',
      length: 'Tag darf nicht länger als 50 Zeichen sein',
      regex: 'Tag enthält ungültige Zeichen. Nur Buchstaben, Zahlen, Leerzeichen, Bindestriche und & sind erlaubt.',
    },
  },

  newsletter: {
    subscribe: 'News abonnieren',
    unsubscribe: 'News abbestellen',
    subscribeDialog: {
      title: 'News abonnieren',
      message: 'Möchtest du bei neuen Artikeln per E-Mail an {{email}} benachrichtigt werden?',
    },
    unsubscribeDialog: {
      title: 'News abbestellen',
      message: 'Möchtest du den Newsletter an {{email}} wirklich abbestellen?',
    },
  },

  latestNewsCard: {
    title: 'Neuste Nachrichten',
    noNews: 'Keine neuen Nachrichten vorhanden.',
    showAll: 'Alle Nachrichten anzeigen',
  },

  tablePaginationActions: {
    firstPage: 'erste Seite',
    previousPage: 'vorherige Seite',
    nextPage: 'nächste Seite',
    lastPage: 'letze Seite',
    page: 'Seite',
  },
};

/** @alpha */
export const newsMessagesEnglish = {
  common: {
    locale: 'en-US',
    confirm: 'Confirm',
    cancel: 'Cancel',
    close: 'Close',
    delete: 'Delete',
    edit: 'Edit',
    save: 'Save',
    immediately: 'Immediately',
    loading: 'Loading...',
    loadingImage: 'Loading image...',
    paginationInfo: 'of',
    by: 'by',
  },

  lockDuration: {
    seconds: 'for {{count}} seconds',
    minutes: 'for {{count}} minutes',
    hours: 'for {{count}} hours',
  },

  newsPage: {
    title: 'Newsportal',
    editorial: 'Editorial',
    noNews: 'No news available at the moment.',
    readMore: 'goto article',
    rowsPerPage: 'Articles per page:',
    errors: {
      loadingArticles: 'Error loading articles',
    },
    showMoreTags: 'Show topics',
    showLessTags: 'Hide topics',
    newMembers: {
      title: 'The latest Puzzle Members',
    },
    backToOverview: 'Back to overview',
  },

  newsDetailPage: {
    error: {
      title: 'Error',
    },
    notFound: {
      title: 'Not found',
    },
    backToOverview: 'Back to overview',
    breadcrumbs: {
      news: 'News',
    },
  },

  members: {
    cardTitle: 'New Member(s)',
    cardHelper:
      'You only need to expand and fill out this section if the article is about new members. This will add a thumbnail of the new member to the `The latest Puzzle Members` section on the news overview.',
    cardToggle: 'Show member section',
    nameLabel: 'Member name',
    cardAddButton: 'Add',
    avatarGuide:
      'Instructions to create a member thumbnail (with GIMP) can be found in the Userguide: Docs > Puzzle Backstage > Features > Newsportal > Userguide > Neue Members > Anleitung: Thumbnail erstellen mit GIMP',
    confirmDelete: {
      title: 'Delete member',
      message: 'Really delete member "{{name}}"?',
    },
    validation: {
      nameRequired: 'Name is required',
      imageRequired: 'Image is required',
      imageTooLarge: 'Image must not exceed 500 KB',
      imageDimensions: 'Image must not exceed 200×200 pixels',
    },
    noMembers: 'No members yet.',
    pendingLabel: 'INFO: Will be saved to DB after saving the article',
    helper: 'Image must not exceed 200x200 pixels and 500 KB',
  },

  adminPage: {
    title: 'Editorial',
    newArticle: 'New Article',
    manageTags: 'Manage Tags',
    allArticles: 'All Articles',
    preview: 'Preview',
    searchPlaceholder: 'Search by title, author',
    columns: {
      id: 'ID',
      title: 'Title',
      author: 'Author',
      lock: 'Lock',
      status: 'Status',
      date: 'Date',
      actions: 'Actions',
    },
    status: {
      draft: 'Draft',
      planned: 'Scheduled',
      published: 'Published',
      archived: 'Archived',
    },
    lockInfo: 'Locked by {{user}} since {{time}}',
    confirmPublish: {
      title: 'Publish',
      message: 'Publish "{{title}}" immediately?',
    },
    confirmUnlock: {
      title: 'Remove Lock',
      message:
        'Remove lock from article "{{ articleTitle }}"? The current editor ({{ lockUser }}) may lose unsaved changes!',
    },
    confirmDelete: {
      title: 'Delete',
      message: 'Are you sure you want to delete article "{{ articleTitle }}"?',
    },
    actions: {
      unlock: 'Unlock',
    },
  },

  editPage: {
    titleNew: 'New Article',
    titleEdit: 'Edit Article',
    content: {
      title: 'Content',
      titleLabel: 'Title',
      help: {
        title: 'Userguide & Help for Article Creation',
        userguideLink: 'Detailed information can be found in the',
        userguide: 'Userguide',
        mdEditorInformation:
          'This is a markdown editor. You can use markdown syntax (e.g. for titles) to format your text.',
        imagesInfo: 'Images can be added via drag & drop into the editor. (Maximum file size: {{size}} MB)',
        videosInfo: 'Videos must be inserted in the following format:',
        videoExample: 'example:',
        videoImportant: 'Important: the ! must be included.',
        videoShorts:
          'To insert YouTube Short videos, the video ID must be transferred to the standard command:\n' +
          '![video](https://www.youtube.com/shorts/fzHhiZ5eHNs) becomes\n' +
          '![video](https://www.youtube.com/watch?v=fzHhiZ5eHNs)',
      },
      placeholder: 'Write your text here or drag & drop images...',
    },
    settings: {
      title: 'Settings',
      onBehalfOfLabel: 'On behalf of',
      onBehalfOfHelper: 'Must only be filled out if the article is being published on behalf of another person.',
      tagsLabel: 'Tags',
    },
    publishing: {
      title: 'Publishing',
      statusLabel: 'Status',
      statusDraft: 'Draft (Hidden)',
      statusPlanned: 'Schedule (Timed)',
      statusNow: 'Publish immediately',
      dateTimeLabel: 'Date & Time',
      publishNow: 'Publish now',
      updatePublished: 'Update published Article',
      noNotificationHint: 'No chat or email notifications will be sent when updating the article.',
    },
    alerts: {
      checkTitle: 'To save please enter a title',
      saveFailed: 'Error while saving:',
      uploadFailed: 'Upload failed!',
      imageTooLarge: 'Image is too large.',
      noPermission: 'No permission: You can only edit your own articles.',
    },
    validation: {
      title: {
        mandatory: 'Title is mandatory',
        length: 'Title must not exceed 200 characters',
      },
      onBehalfOf: 'On behalf of must not exceed 100 characters',
    },
  },

  tagsDialog: {
    title: 'Manage Tags',
    newTagLabel: 'New tag (e.g. HR, Event)',
    add: 'Add',
    loadError: 'Error loading:',
    noTags: 'No tags available yet.',
    confirmDelete: {
      title: 'Delete Tag',
      message: 'Really delete tag "{{name}}"?',
    },
    validations: {
      mandatory: 'Tag is mandatory',
      length: 'Tag must not exceed 50 characters',
      regex: 'Tag contains invalid characters. Only letters, numbers, spaces, hyphens, and & are allowed.',
    },
  },

  newsletter: {
    subscribe: 'Subscribe to News',
    unsubscribe: 'Unsubscribe from News',
    subscribeDialog: {
      title: 'Subscribe to News',
      message: 'Would you like to be notified by email at {{email}} when new articles are published?',
    },
    unsubscribeDialog: {
      title: 'Unsubscribe from News',
      message: 'Are you sure you want to unsubscribe {{email}} from the News?',
    },
  },

  latestNewsCard: {
    title: 'Latest News',
    noNews: 'No news available.',
    showAll: 'Show all news',
  },

  tablePaginationActions: {
    firstPage: 'first Page',
    previousPage: 'previous Page',
    nextPage: 'next Page',
    lastPage: 'last Page',
    page: 'Page',
  },
};

/** @alpha */
export const newsTranslationRef = createTranslationRef({
  id: 'plugin.news',
  messages: newsMessagesGerman,
});
